import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SubscriptionTier, getSubscriptionTier } from '../config/subscriptions';
import SubscriptionPlans from './SubscriptionPlans';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';

type Step = 'plans' | 'payment-and-account' | 'processing' | 'success';

interface PaymentIntent {
  client_secret: string;
  payment_intent_id: string;
  amount: number;
  original_amount: number;
  discount_amount: number;
  currency: string;
  customer_id: string;
}

const SignupWithElements: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('plans');
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null);
  // Note: Coupon codes are now handled natively by Stripe Checkout

  const handleTierSelection = (tierId: string) => {
    const tier = getSubscriptionTier(tierId);
    if (!tier) {
      setError('Invalid subscription tier selected');
      return;
    }
    
    setSelectedTier(tier);
    setStep('payment-and-account');
  };

  const createCheckoutSession = async () => {
    if (!selectedTier || !email) {
      setError('Please complete all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const checkoutUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:8788' 
        : 'https://eightythreelabs-subscription-checkout.yahya-darman-work.workers.dev';

      const response = await fetch(`${checkoutUrl}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tier: selectedTier.id,
          customer_email: email,
          success_url: `${window.location.origin}/signup-success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/signup`
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Store signup data for account creation after payment
      const signupData = {
        email,
        password,
        tier: selectedTier.id,
        agreedToTerms
      };
      localStorage.setItem('eightythreelabs_signup_data', JSON.stringify(signupData));

      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout session creation failed:', err);
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('Unable to connect to payment services. Please check your internet connection or disable ad blockers.');
      } else if (err instanceof Error) {
        if (err.message.includes('Stripe configuration missing') || err.message.includes('Failed to create checkout session')) {
          setError('Payment system is temporarily unavailable. Our development team has been notified. Please try again later or contact support.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to set up payment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    if (!selectedTier) {
      setError('Tier information missing');
      return;
    }

    setStep('processing');

    try {
      const response = await fetch('https://eightythreelabs-confirm-payment-signup.yahya-darman-work.workers.dev/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_intent_id: paymentIntentId,
          email,
          password,
          tier: selectedTier.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create account');
      }

      const result = await response.json();
      
      if (result.success) {
        setStep('success');
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        throw new Error(result.error || 'Account creation failed');
      }
    } catch (error) {
      console.error('Account creation failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to create account. Please contact support.');
      setStep('payment-and-account');
    }
  };

  const handlePaymentError = (error: string) => {
    setError(error);
  };

  // Removed handleCouponChange - promotion codes now handled by Stripe Checkout

  const validateForm = () => {
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleContinueToPayment = async () => {
    if (!validateForm()) return;
    await createCheckoutSession();
  };

  const renderPlanSelection = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Get Elite Access
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          Unlock all EightyThreeLabs features
        </p>
      </div>
      
      <SubscriptionPlans
        onSelectPlan={(tier: SubscriptionTier) => handleTierSelection(tier.id)}
        selectedTier={selectedTier?.id}
      />
    </div>
  );

  const renderPaymentAndAccount = () => {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Complete Your Signup
          </h1>
          <div className="text-gray-600 dark:text-gray-300">
            <p>{selectedTier?.name}</p>
            <p>{selectedTier?.priceDisplay}/year</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex">
              <div className="text-red-800 dark:text-red-200 text-sm">{error}</div>
            </div>
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); handleContinueToPayment(); }} className="space-y-8">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Account Information</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="your@email.com"
                  autoComplete="username"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="Minimum 6 characters"
                  autoComplete="new-password"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="terms"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                required
              />
              <label htmlFor="terms" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                I agree to the{' '}
                <a href="/terms" target="_blank" className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" target="_blank" className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300">
                  Privacy Policy
                </a>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              loading
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700'
            }`}
          >
            {loading ? 'Redirecting to payment...' : 'Continue to Stripe Checkout'}
          </button>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">Secure Checkout with Promotion Codes</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  You'll be redirected to Stripe's secure checkout page where you can enter promotion codes and complete your payment safely.
                </p>
              </div>
            </div>
          </div>
        </form>

        <button
          onClick={() => {
            setStep('plans');
            setPaymentIntent(null);
            setError('');
          }}
          className="w-full py-2 px-4 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors mt-4"
        >
          ← Back to Plans
        </button>
      </div>
    );
  };

  const renderProcessing = () => (
    <div className="max-w-md mx-auto text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-6"></div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Creating Your Account...</h2>
      <p className="text-gray-600 dark:text-gray-300">
        Please wait while we set up your subscription and create your account.
        This may take a few moments.
      </p>
    </div>
  );

  const renderSuccess = () => (
    <div className="max-w-md mx-auto text-center">
      <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-8 h-8 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Welcome to EightyThreeLabs!</h2>
      <p className="text-gray-600 dark:text-gray-300 mb-6">
        Your account has been created successfully. You'll be redirected to your dashboard shortly.
      </p>
      <div className="animate-pulse text-sm text-gray-500 dark:text-gray-400">
        Redirecting to dashboard...
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <PublicHeader showAuthLinks={false} />
      
      <main className="flex-1 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {step === 'plans' && renderPlanSelection()}
          {step === 'payment-and-account' && renderPaymentAndAccount()}
          {step === 'processing' && renderProcessing()}
          {step === 'success' && renderSuccess()}
        </div>
      </main>
      
      <PublicFooter />
    </div>
  );
};

export default SignupWithElements;